import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from './lib/auth'
import { ConfirmProvider } from './components/ConfirmDialog'
import App from './App'
import './styles/shared.css'

// Debug error boundary — renders the error visibly so we don't get silent
// blank pages. Delete once the app is stable.
class DebugBoundary extends React.Component<{ children: React.ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) { return { error } }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('🛑 React render error:', error, info)
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ position: 'fixed', inset: 0, padding: 40, background: '#fff5f5', color: '#991b1b', fontFamily: 'monospace', overflow: 'auto', zIndex: 9999 }}>
          <h1 style={{ marginBottom: 12, fontFamily: 'sans-serif' }}>React render error</h1>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{String(this.state.error?.stack || this.state.error)}</pre>
        </div>
      )
    }
    return this.props.children
  }
}

// Also catch unhandled rejections (promises that reject without .catch) and
// global JS errors — both show as blank pages otherwise.
window.addEventListener('error', (e) => console.error('🛑 window.error:', e.error || e.message))
window.addEventListener('unhandledrejection', (e) => console.error('🛑 unhandledrejection:', e.reason))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 5 min stale-while-revalidate matches the vanilla `authFetch` caching
      // expectations users are used to.
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <DebugBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <ConfirmProvider>
              <App />
            </ConfirmProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </DebugBoundary>
  </React.StrictMode>,
)
